import React, { useState, useCallback } from 'react';
import {
  Card,
  FormLayout,
  TextField,
  Button,
  Banner,
  Text,
  Link,
  BlockStack,
  InlineStack,
  Box,
} from '@shopify/polaris';
import { authApi } from '../api/auth-api';
import Logo from '../../../components/logo';

/**
 * ForgotPasswordPage - Password reset request page
 *
 * Allows users to request a password reset link by entering their email.
 * Sends reset link via email and displays success/error messages.
 *
 * @example
 * <Route path="/password/reset" element={<ForgotPasswordPage />} />
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleEmailChange = useCallback((value) => setEmail(value), []);

  const handleSubmit = useCallback(async () => {
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await authApi.forgotPassword(email);
      setSuccess(
        response.data?.status || 'We have emailed your password reset link!'
      );
      setEmail('');
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.errors?.email?.[0] ||
        'Unable to send reset link. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <Box
      background="bg-surface-secondary"
      minHeight="100vh"
      padding={{ xs: '400', md: '800' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 40px)',
        }}
      >
        <div style={{ width: '100%', maxWidth: '400px' }}>
          <BlockStack gap="600">
            <BlockStack gap="200">
              <Logo size="medium" showText align="center" />
              <Text variant="bodyMd" as="p" alignment="center" tone="subdued">
                Reset Password
              </Text>
            </BlockStack>

            <Card>
              <BlockStack gap="400">
                {error && (
                  <Banner tone="critical" onDismiss={() => setError('')}>
                    {error}
                  </Banner>
                )}

                {success && (
                  <Banner tone="success" onDismiss={() => setSuccess('')}>
                    {success}
                  </Banner>
                )}

                <FormLayout>
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    autoComplete="email"
                    placeholder="admin@example.com"
                    disabled={isLoading}
                    helpText="Enter your email and we'll send you a reset link"
                    onKeyDown={handleKeyDown}
                  />

                  <Button
                    variant="primary"
                    fullWidth
                    loading={isLoading}
                    onClick={handleSubmit}
                  >
                    Send Reset Link
                  </Button>
                </FormLayout>

                <InlineStack align="center">
                  <Link url="/login" removeUnderline>
                    ← Back to login
                  </Link>
                </InlineStack>
              </BlockStack>
            </Card>
          </BlockStack>
        </div>
      </div>
    </Box>
  );
}
